import React, { useState, useRef, useEffect } from 'react'
import { FiMoreHorizontal, FiStar } from 'react-icons/fi'
import { FaStar } from 'react-icons/fa'
import { styled } from 'styled-components'
import { useRouter } from 'next/router'

import useIsMobile from '@/util/useIsMobile'

import { PostSwiper } from 'src/components/PostSwiper'
import { RecentPostsSection } from '@/components/Discover/RecentPostsSection'
import { CreatorProfilesSection } from '@/components/Discover/CreatorProfilesSection'
import { FavoritesToggle } from '@/components/Discover/FavoritesToggle'

export const PostContentDisplay = ({
  post,
  userData,
  locked,
  profile,
  showRelatedSections = true,
  wide = false,
  hideProfile = false
}) => {
  const router = useRouter()
  const isMobile = useIsMobile()
  const [showDropdown, setShowDropdown] = useState(false)
  const [favorited, setFavorited] = useState(false)
  const [copied, setCopied] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
      <>
        <TopSection className={`mb:w-full ${wide ? 'dt:w-full dt:max-w-2xl' : 'dt:w-3/5'} mx-auto pt-5`}>
          {!hideProfile && (
            <UserInfo>
              <LeftSide>
                <ProfileIcon
                  className="cursor-pointer"
                  src={profile.avatar}
                  onClick={() => router.push(`/discover/profile/${profile.authorUsername}`)} />
                <NameSection>
                  <h1 className="text-base font-soehne-kraftig">{profile.headline}</h1>
                  <Text className="tracking-wide text-sm font-soehne-leight text-bold">
                    @{profile.authorUsername}
                  </Text>
                </NameSection>
              </LeftSide>

              <RightSide ref={dropdownRef}>
                {userData && <FavoritesToggle postId={post.id} /> }
                <FiMoreHorizontal
                  size={22}
                  className="cursor-pointer text-neutral-600 hover:text-black transition"
                  onClick={() => setShowDropdown(!showDropdown)}
                />
                {showDropdown && (
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => {
                        const url = `${window.location.origin}/discover/${post.id}`
                        navigator.clipboard.writeText(url)
                        setCopied(true)
                        setTimeout(() => setCopied(false), 1000)
                        setShowDropdown(false)
                      }}
                    >
                      Copy Link
                    </DropdownItem>
                  </DropdownMenu>
                )}
                {copied && (
                  <CopiedMessage>Link copied!</CopiedMessage>
                )}
              </RightSide>
            </UserInfo>
          )}
  
          <div className="mt-5 relative">
            <h1
              className="font-soehne-kraftig tracking-wide text-xl dt:text-2xl dt:mt-2"
              style={{ lineHeight: isMobile ? undefined : 1.25 }}
            >
              {post.title}
            </h1>
  
            <div
              className="mt-2 mb-2 font-soehne-leight text-base dt:text-lg"
              dangerouslySetInnerHTML={{ __html: post.description }}
            ></div>
  
            <div className="mt-2 flex items-center text-sm font-soehne-kraftig text-neutral-500">
              📍 {post.location}
            </div>
          </div>
        </TopSection>
        <div className={`mb:w-full ${wide ? 'dt:w-full dt:max-w-2xl' : 'dt:w-3/5'} mx-auto pt-5`}>
          <PostSwiper uploads={post.uploads} post={post} locked={locked} />
  
          { showRelatedSections && (
            <>
              <CreatorProfilesSection excludeUserId={post.authorId} />
              <RecentPostsSection currentPostId={post.id} />
            </>
          )}
        </div>
      </>
    )
}

const Text = styled.span.attrs({ className: 'font-soehne-leight' })``

const UserInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`

const LeftSide = styled.div`
  display: flex;
  align-items: center;
`

const RightSide = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const TopSection = styled.div`
  @media (max-width: 768px) {
      padding-left: 10px;
      padding-right: 10px;
  }
`

const ProfileIcon = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: block;
  margin-right: 10px;
  object-fit: cover;
  object-position: top;
`

const DropdownMenu = styled.div`
  position: absolute;
  top: 28px;
  right: 0;
  background: white;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  border-radius: 6px;
  padding: 8px 0;
  z-index: 9999;
  min-width: 140px;
  display: flex;
  flex-direction: column;
`

const DropdownItem = styled.div`
  padding: 8px 12px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #f5f5f5;
  }
`

const CopiedMessage = styled.div`
  position: absolute;
  top: 32px;
  right: 0;
  background: black;
  color: white;
  padding: 6px 10px;
  border-radius: 5px;
  font-size: 12px;
  z-index: 9999;
  white-space: nowrap;
`

const NameSection = styled.div``

